import axios, {AxiosResponse} from 'axios'
import {API_URL, ID, Response} from '../../../../../../_metronic/helpers'
import {
  ExchangeAnnouncementChannel,
  ExchangeAnnouncementSource,
  ExchangeAnnouncementStatus,
  Invoice,
  InvoiceBuyer,
  InvoiceExchangeAnnouncementSummary,
  InvoiceListSendStatuses,
  InvoiceListSummary,
  InvoicesQueryResponse,
} from './_models'

import {PRINT_LAYOUT_VERSION} from '../../invoice-print/core/printLayout'
import type {InvoicePrintPage} from '../../invoice-print/core/printPagePreference'

const INVOICE_URL = `${API_URL}/invoices`
const GET_INVOICES_URL = `${API_URL}/invoices/query`
const INVOICE_LIST_SUMMARY_STALE_TIME_MS = 60 * 1000
const INVOICE_LIST_SUMMARY_CACHE_TIME_MS = 5 * 60 * 1000
const INVOICE_BULK_SEND_REGISTER_TIMEOUT_MS = 15 * 1000
const INVOICE_BULK_SEND_STATUS_TIMEOUT_MS = 10 * 1000
const INVOICE_DATE_CHANGE_REGISTER_TIMEOUT_MS = 15 * 1000
const INVOICE_DATE_CHANGE_STATUS_TIMEOUT_MS = 10 * 1000

export type TaxFolderImportRowResult = {
  rowNumber: number
  taxId?: string
  folderStatus?: string
  result: string
  updated: boolean
  hasDiscrepancy: boolean
  differences: TaxFolderFieldDifference[]
}

export type TaxFolderFieldDifference = {
  key?: string
  field: string
  localValue: string
  folderValue: string
  actionable?: boolean
}

export type TaxIdOwnershipValidationResult = {
  isValid: boolean
  fiscalIdConfigured: boolean
  fiscalId?: string
  message: string
  canCreateReferral?: boolean
  inquiryAttempted?: boolean
  requestedTaxId?: string
  resolvedReferenceTaxId?: string
  referenceInvoiceId?: number
  referenceInvoiceHash?: string
  referenceStatus?: string
  referenceStatusText?: string
  referenceChanged?: boolean
}

export type ReferenceActionPreparationResult = {
  allowed: boolean
  refreshed: boolean
  requiresManualStatusUpdate: boolean
  requiresCachedStatusConfirmation?: boolean
  usedCachedStatus?: boolean
  action: 2 | 3 | 4
  message: string
  canAmend: boolean
  canReturn: boolean
  canCancel: boolean
  activeReferenceInvoiceId?: number
  referenceInvoiceId?: number
  referenceInvoiceHash?: string
  referenceTaxId?: string
  referenceStatus?: string
  referenceStatusText?: string
  checkedAtUtc?: string
  // Machine reason of a blocked action, e.g. EXCHANGE_ANNOUNCEMENT_CONFIRMED (pattern 11).
  reasonCode?: string
  exchangeAnnouncement?: InvoiceExchangeAnnouncementSummary
}

export type ExchangeAnnouncementPendingCorrectionDraft = {
  invoiceId: number
  no?: string
  hash?: string
  // True for a correction already submitted for sending (phase 2): confirming does not recall it,
  // it only stops any re-send. Absent/false is an unsent draft (older backends omit the field).
  inFlight?: boolean
}

// GET/POST api/invoices/{invoiceId}/exchange-announcement (pattern 11 only).
export type InvoiceExchangeAnnouncementStatusResult = {
  invoiceId: number
  originTaxId: string
  rootInvoiceId: number
  status: ExchangeAnnouncementStatus
  source?: ExchangeAnnouncementSource
  channel?: ExchangeAnnouncementChannel
  setByUserId?: number
  setBy?: string
  setAtUtc?: string
  setAt?: string
  saleAnnNo?: string
  saleAnnDate?: string
  note?: string
  // Row version (base64); sent back as expectedVersion so a concurrent change is refused.
  version?: string
  canChange: boolean
  // Pattern-11 corrections of the chain not yet accepted: unsent drafts (confirming freezes them)
  // and, with inFlight, corrections already submitted for sending (confirming stops a re-send).
  pendingCorrectionDrafts: ExchangeAnnouncementPendingCorrectionDraft[]
}

export type SetExchangeAnnouncementStatusRequest = {
  status: 'confirmed' | 'notConfirmed'
  note?: string
  expectedVersion?: string
}

export type InvoiceSendResultInquiryOutcome =
  | 'Succeeded'
  | 'InProgress'
  | 'Failed'
  | 'NotFound'
  | 'Unknown'

export type InvoiceSendResultInquiryResult = {
  invoiceId: number
  taxId?: string
  referenceNumber: string
  inquiryStatus: string
  outcome: InvoiceSendResultInquiryOutcome
  message: string
  error?: string
  warning?: string
  isFinal: boolean
  isSuccess?: boolean
  invoicePhaseId: number
  taxGovStatus?: number
  latestDateTime: string
}

/** One invoice the on-premises «همگام‌سازی با سوئیچ مرکزی» changed or left for review. */
export type SwitchRegistrationSyncItem = {
  invoiceId: number
  number?: string
  outcome: 'Registered' | 'Rejected' | 'IdentityUpdated' | 'NeedsReview' | 'Failed'
  previousTaxId?: string
  taxId?: string
  message?: string
  statusSettled?: boolean
}

/** One page of POST api/invoices/switch-sync; the panel asks again from nextAfterInvoiceId until done. */
export type SwitchRegistrationSyncResult = {
  checkedCount: number
  registered: number
  rejected: number
  identityUpdated: number
  needsReview: number
  pending: number
  unchanged: number
  notAtSwitch: number
  failed: number
  nextAfterInvoiceId?: number | null
  done: boolean
  items: SwitchRegistrationSyncItem[]
}

// After a 0300101 rejection only an administrator gives the invoice a new serial and TaxId.
export type TaxIdConflictRenewalStatus = {
  invoiceId: number
  eligible: boolean
  code?: string
  message: string
}

export type TaxIdConflictRenewalResult = {
  invoiceId: number
  taxId: string
  previousTaxId?: string
  serial: number
}

export type TaxFolderImportResult = {
  isSuccess: boolean
  message: string
  fileName: string
  totalRows: number
  validRows: number
  matchedRows: number
  matchedInvoices: number
  updatedRows: number
  updatedInvoices: number
  unchangedRows: number
  notFoundRows: number
  invalidRows: number
  duplicateRows: number
  discrepancyRows: number
  previewOnly: boolean
  requiresConfirmation: boolean
  token?: string
  details: TaxFolderImportRowResult[]
}

export type TaxFolderReferenceCandidate = {
  rowNumber: number
  taxId: string
  folderStatus?: string
  invoiceHash?: string
  invoiceNo?: string
  invoiceDate?: string
  buyerName?: string
  subject?: number
  canLoadDetails: boolean
  // From the Excel row when the invoice is not in the panel.
  subjectTitle?: string
  invoiceTypeTitle?: string
  templateTitle?: string
  netPrice?: number
  // Whether its header can be registered from this file, and why not otherwise.
  canRegister?: boolean
  registerMessage?: string
  // Shown under the reference field after the header was registered from the file (panel only).
  notice?: string
}

export type TaxFolderReferenceResult = {
  fileName: string
  totalRows: number
  invalidRows: number
  duplicateRows: number
  candidates: TaxFolderReferenceCandidate[]
  missingHeaderColumns?: string[]
}

export type TaxFolderReferenceRegisterResult = {
  isSuccess: boolean
  message: string
  alreadyRegistered: boolean
  createdInvoices: number
  registeredTaxIds: string[]
  candidate: TaxFolderReferenceCandidate
}

export type TaxFolderPdfInvoiceItem = {
  RowNumber: number
  StuffId: string
  Description: string
  Unit: string
  Quantity: number
  UnitPrice: number
  PriceBeforeDiscount: number
  Discount: number
  VatPrice: number
  VatRate: number
  OtherTaxPrice: number
  OtherTaxRate: number
  TotalPrice: number
}

export type TaxFolderPdfInvoice = {
  FileName: string
  TaxId: string
  SourceTaxId?: string
  No: string
  Date: string
  Type: number
  Template: number
  Subject: number
  SubjectTitle: string
  TemplateTitle: string
  SellerName: string
  SellerEconomicCode?: string
  SellerNationalCode?: string
  BuyerName?: string
  BuyerEconomicCode?: string
  BuyerNationalCode?: string
  BuyerZipCode?: string
  BuyerAddress?: string
  SettlementTitle: string
  FolderStatus: string
  NetPrice: number
  Items: TaxFolderPdfInvoiceItem[]
  Warnings: string[]
}

export type TaxFolderPdfPreviewResult = {
  token: string
  invoice: TaxFolderPdfInvoice
  alreadyExists: boolean
  existingInvoiceHash?: string
  hasDiscrepancy: boolean
  differences: TaxFolderFieldDifference[]
}

export type TaxFolderPdfConfirmResult = {
  created: boolean
  updated: boolean
  taxId: string
  invoiceId: number
  invoiceHash: string
  phaseId: number
  message: string
}

export type TaxFolderArchiveFieldResult = {
  label: string
  value: string
}

export type TaxFolderArchiveRowResult = {
  rowNumber: number
  taxId: string
  folderStatus: string
  action:
    | 'create'
    | 'update-status'
    | 'compare-only'
    | 'created'
    | 'status-updated'
    | 'selected-updated'
    | 'unchanged'
    | 'skipped'
    // A row the background import refused on its own; its reason is in result.
    | 'failed'
  result: string
  changed: boolean
  hasDiscrepancy: boolean
  invoiceId?: number
  buyerCustomerId?: number
  buyerName?: string
  existingBuyerName?: string
  subject: number
  subjectTitle?: string
  sourceTaxId?: string
  referenceAvailable: boolean
  invoiceTypeTitle?: string
  invoiceType: number
  templateTitle?: string
  template: number
  invoiceDate?: string
  buyerIdentity?: string
  buyerEconomicCode?: string
  buyerIsLegal: boolean
  settlementTitle?: string
  settlement: number
  priceBeforeTax: number
  vatPrice: number
  otherTaxPrice: number
  netPrice: number
  remainingAmount: number
  article6Status?: string
  itemCount: number
  paymentCount: number
  fields: TaxFolderArchiveFieldResult[]
  items: TaxFolderArchiveItemResult[]
  payments: TaxFolderArchivePaymentResult[]
  inquiryStatus?: string
  inquiryError?: string
  differences: TaxFolderFieldDifference[]
  // Not a cancellation, yet the ZIP has no item rows for it.
  itemsMissingInFile?: boolean
  // Why its items cannot be registered or completed from this ZIP.
  itemsProblem?: string
  // A new invoice that would be registered without items: confirming needs acknowledgeMissingItems.
  requiresItemsAcknowledgement?: boolean
  warnings?: string[]
  itemsOutcome?: string
  itemsAdded?: number
  itemsLinked?: number
  paymentsAdded?: number
  // A referral whose reference is not in the panel or this ZIP: imported with a warning, linked later.
  referenceWaiting?: boolean
  // Referrals already in the panel that wait for this invoice as their reference.
  waitingReferralCount?: number
}

export type TaxFolderArchiveItemResult = {
  rowNumber: number
  stuffId?: string
  description?: string
  priceBeforeTax: number
  vatPrice: number
  otherTaxPrice: number
  vatRate: number
  totalPrice: number
  fields: TaxFolderArchiveFieldResult[]
}

export type TaxFolderArchivePaymentResult = {
  rowNumber: number
  paymentDate?: string
  price: number
  method?: string
  status?: string
  fields: TaxFolderArchiveFieldResult[]
}

export type TaxFolderArchiveRowSelection = {
  taxId: string
  createInvoice: boolean
  includeItems: boolean
  includePayments: boolean
  changes: string[]
  buyerVersionMode: 'inPlace' | 'newVersion'
  buyerInvoiceScope: 'futureOnly' | 'unsent' | 'all'
}

export type TaxFolderArchiveFiscalMemoryEnvironment = 'Production' | 'Sandbox'

export type TaxFolderArchiveFiscalMemoryOption = {
  environment: TaxFolderArchiveFiscalMemoryEnvironment
  title: string
  // The business already has this memory in that environment (registeredDisabled: but it is disabled).
  registered: boolean
  registeredDisabled?: boolean
  // Not in the business yet; the organisation confirmed it and the confirm adds it.
  willBeAdded: boolean
  note?: string
}

/**
 * One fiscal memory (the first six characters of the file's tax ids). The preview asks which environment the file is
 * for when more than one may apply (requiresChoice); after the confirm it says where the invoices were bound.
 */
export type TaxFolderArchiveFiscalMemoryResult = {
  fiscalId: string
  invoiceCount: number
  requiresChoice: boolean
  environment?: TaxFolderArchiveFiscalMemoryEnvironment
  environmentTitle?: string
  willBeAdded?: boolean
  added?: boolean
  // Invoices of the file imported earlier on the other environment's memory, moved to this one.
  reboundInvoices?: number
  message?: string
  options: TaxFolderArchiveFiscalMemoryOption[]
}

export type TaxFolderArchiveFiscalMemoryChoice = {
  fiscalId: string
  environment: TaxFolderArchiveFiscalMemoryEnvironment
}

export type TaxFolderArchiveConfirmRequest = {
  token: string
  selections: TaxFolderArchiveRowSelection[]
  buyerVersionMode?: 'inPlace' | 'newVersion'
  buyerInvoiceScope?: 'futureOnly' | 'unsent' | 'all'
  // The user saw that some selected invoices have no items in the ZIP and still registers them.
  acknowledgeMissingItems?: boolean
  // The environment chosen for each fiscal memory the preview asked about.
  fiscalMemoryChoices?: TaxFolderArchiveFiscalMemoryChoice[]
  // Agreed to the fees of «ورود ZIP کارپوشه» (backend 1.0.0.640+; 409 otherwise).
  confirmCharge?: boolean
}

export type TaxFolderArchiveImportResult = {
  isSuccess: boolean
  message: string
  fileName: string
  totalRows: number
  existingRows: number
  createRows: number
  updatedInvoices: number
  createdInvoices: number
  unchangedRows: number
  discrepancyRows: number
  itemRows: number
  paymentRows: number
  selectedRows: number
  skippedRows: number
  buyerUpdates: number
  inquiryTotal: number
  inquirySucceeded: number
  inquiryFailed: number
  hasItemsFile: boolean
  hasPaymentsFile: boolean
  previewOnly: boolean
  requiresConfirmation: boolean
  token?: string
  details: TaxFolderArchiveRowResult[]
  warnings?: string[]
  missingItemRows?: number
  completedInvoices?: number
  waitingReferenceRows?: number
  attachedReferrals?: number
  // Rows the import refused on their own; their reason is on the row.
  failedRows?: number
  // The background import a confirm hands the file to. A server older than it sends none of these.
  jobId?: string
  jobStatus?: TaxFolderArchiveJobStatus
  jobPhase?: string
  processedRows?: number
  jobError?: string
  jobStartedAt?: string
  jobCompletedAt?: string
  // The fiscal memories of the file; a server older than 1.0.0.621 sends none.
  fiscalMemories?: TaxFolderArchiveFiscalMemoryResult[]
  requiresFiscalMemoryChoice?: boolean
  reboundInvoices?: number
}

export type TaxFolderArchiveJobStatus = 'queued' | 'running' | 'done' | 'failed'

const normalizeTaxFolderHeader = (value: string) =>
  value
    .replace(/[\s\u200c\u200f\u202a-\u202e_-]+/g, '')
    .replace(/ي/g, 'ی')
    .replace(/ك/g, 'ک')
    .toLocaleLowerCase('fa-IR')

const getTaxFolderText = (...values: unknown[]) => {
  const value = values.find(
    (item) =>
      item !== undefined &&
      item !== null &&
      `${item}`.trim() !== '' &&
      `${item}`.trim().toLowerCase() !== 'undefined' &&
      `${item}`.trim().toLowerCase() !== 'null'
  )
  return value === undefined ? undefined : `${value}`.trim()
}

const getTaxFolderField = (
  fields: TaxFolderArchiveFieldResult[] | undefined,
  ...labels: string[]
) => {
  const normalizedLabels = labels.map(normalizeTaxFolderHeader)
  return getTaxFolderText(
    (fields || []).find((field) =>
      normalizedLabels.includes(normalizeTaxFolderHeader(field.label || ''))
    )?.value
  )
}

const getTaxFolderEnumValue = (
  currentValue: unknown,
  title: string | undefined,
  titles: Record<string, number>
) => {
  const numericValue = Number(currentValue)
  if (Number.isFinite(numericValue) && numericValue > 0) return numericValue
  return title ? titles[normalizeTaxFolderHeader(title)] || 0 : 0
}

const normalizeTaxFolderArchiveRow = (
  source: TaxFolderArchiveRowResult
): TaxFolderArchiveRowResult => {
  const legacy = source as unknown as Record<string, unknown>
  const fields = Array.isArray(source.fields) ? source.fields : []
  const invoiceTypeTitle = getTaxFolderText(
    source.invoiceTypeTitle,
    legacy.InvoiceTypeTitle,
    getTaxFolderField(fields, 'نوع صورتحساب')
  )
  const templateTitle = getTaxFolderText(
    source.templateTitle,
    legacy.TemplateTitle,
    getTaxFolderField(fields, 'الگو صورتحساب', 'الگوی صورتحساب')
  )
  const subjectTitle = getTaxFolderText(
    source.subjectTitle,
    legacy.SubjectTitle,
    getTaxFolderField(fields, 'موضوع صورتحساب')
  )

  const items = (Array.isArray(source.items) ? source.items : []).map((item) => ({
    ...item,
    fields: Array.isArray(item.fields) ? item.fields : [],
  }))
  const payments = (Array.isArray(source.payments) ? source.payments : []).map((payment) => ({
    ...payment,
    fields: Array.isArray(payment.fields) ? payment.fields : [],
  }))

  return {
    ...source,
    invoiceTypeTitle,
    invoiceType: getTaxFolderEnumValue(source.invoiceType, invoiceTypeTitle, {
      اول: 1,
      دوم: 2,
      سوم: 3,
    }),
    templateTitle,
    template: getTaxFolderEnumValue(source.template, templateTitle, {
      فروش: 1,
    }),
    subjectTitle,
    subject: getTaxFolderEnumValue(source.subject, subjectTitle, {
      اصلی: 1,
      اصلاحی: 2,
      ابطالی: 3,
      برگشتازفروش: 4,
    }),
    invoiceDate: getTaxFolderText(
      source.invoiceDate,
      legacy.InvoiceDate,
      getTaxFolderField(fields, 'تاریخ صدور صورتحساب')
    ),
    settlementTitle: getTaxFolderText(
      source.settlementTitle,
      legacy.SettlementTitle,
      getTaxFolderField(fields, 'روش تسویه')
    ),
    fields,
    items,
    payments,
    itemCount: Number.isFinite(Number(source.itemCount)) ? Number(source.itemCount) : items.length,
    paymentCount: Number.isFinite(Number(source.paymentCount))
      ? Number(source.paymentCount)
      : payments.length,
    differences: Array.isArray(source.differences) ? source.differences : [],
    warnings: Array.isArray(source.warnings) ? source.warnings : [],
  }
}

const normalizeTaxFolderArchiveResult = (
  result: TaxFolderArchiveImportResult
): TaxFolderArchiveImportResult => ({
  ...result,
  details: (result.details || []).map(normalizeTaxFolderArchiveRow),
  warnings: Array.isArray(result.warnings) ? result.warnings : [],
  fiscalMemories: Array.isArray(result.fiscalMemories)
    ? result.fiscalMemories.map((memory) => ({
        ...memory,
        options: Array.isArray(memory.options) ? memory.options : [],
      }))
    : [],
})

const getInvoiceListSummaryQueryKey = (
  companyId: number,
  baseMode: number,
  invoiceSeason?: string
) => ['invoice-list-summary', companyId, baseMode, invoiceSeason || 'latest'] as const

// Under the summary's key prefix, so every invalidateQueries('invoice-list-summary') after a
// send, delete or inquiry recounts the status cards too.
const getInvoiceListSendStatusesQueryKey = (companyId: number, invoiceSeason?: string) =>
  ['invoice-list-summary', companyId, 1, invoiceSeason || 'latest', 'send-statuses'] as const

const shouldRetryInvoiceListSummary = (failureCount: number, error: any) =>
  !error?.response && failureCount < 1

export type InvoiceFiscalMemoryOption = {
  id: number
  title?: string
  name?: string
  uniqueTaxId?: string
  taxid?: string
  isActive?: boolean
  isDefault?: boolean
  isEnabled?: boolean
  environmentTitle?: string
}

const getInvoiceFiscalMemoriesQueryKey = (companyId: number) =>
  ['invoice-list-fiscal-memories', companyId] as const

const getInvoiceFiscalMemories = (): Promise<InvoiceFiscalMemoryOption[]> => {
  return axios
    .get(`${API_URL}/taxpayer/fiscalIds/brief`)
    .then((d: AxiosResponse<InvoiceFiscalMemoryOption[]>) => d.data || [])
}

const getInvoices = (query: string, signal?: AbortSignal): Promise<InvoicesQueryResponse> => {
  return axios
    .get(`${GET_INVOICES_URL}?${query}`, {signal})
    .then((d: AxiosResponse<InvoicesQueryResponse>) => d.data)
}

/** «سوابق صورتحساب»: the invoices of the chain under one list row (GET api/invoices/subinvoices/{hash}). */
const getSubInvoices = (hash: string): Promise<Invoice[]> => {
  return axios
    .get(`${INVOICE_URL}/subinvoices/${hash}`)
    .then((d: AxiosResponse<Invoice[]>) => d.data || [])
}

export type InvoiceBulkSendRequest = {
  filter: Record<string, unknown>
  chooseType: number
  selected: Array<ID>
  basemode?: number
  invoiceSeason?: string
  operationId: string
}

export type InvoiceBulkSendAcceptedResponse = {
  operationId: string
  jobId: string | number
  status: 'Registered' | 'Completed' | 'CompletedWithErrors' | 'CompletedWithUnresolved'
  statusLabel: string
  message: string
  submittedAtUtc: string
  statusUrl: string
  logId?: number
}

export type InvoiceBulkSendJobItem = {
  invoiceId: number
  invoiceNo?: string
  status: string
  statusLabel: string
  errorCode?: string
  errorMessage?: string
}

export type InvoiceBulkSendJobStatus = {
  operationId: string
  jobId: string | number
  status: string
  statusLabel: string
  message: string
  submittedAtUtc: string
  startedAtUtc?: string
  completedAtUtc?: string
  discoveredCount: number
  processedCount: number
  registeredCount: number
  queuedCount: number
  submittedCount: number
  rejectedCount: number
  errorCount: number
  // Sent, but the tax system's answer was lost; settled by inquiry. Not part of errorCount.
  // Optional so older backends that do not report it keep working.
  unresolvedCount?: number
  // Of a finished request's unresolved invoices: those whose outcome is still unknown, and those
  // settled only by an earlier rejection of the same tax id, which return to the draft phase to be
  // sent again. Null or missing until the request is finished (or on an older backend): read
  // unresolvedCount instead.
  openUnresolvedCount?: number | null
  lostAfterRejectionCount?: number | null
  canRetry: boolean
  recoverableCount: number
  progressPercent: number
  isTerminal: boolean
  logId?: number
  items: InvoiceBulkSendJobItem[]
  page: number
  pageSize: number
  totalItems: number
}

export type InvoiceDateChangeRequest = {
  filter: Record<string, unknown>
  chooseType: number
  selected: Array<ID>
  basemode?: number
  invoiceSeason?: string
  selectedDateTime: string
  selectedInvoiceNo?: string
  isArticle9?: true
  indati2m?: number
  operationId: string
}

export type InvoiceDateChangeAcceptedResponse = {
  operationId: string
  jobId: string | number
  logId: number
  status: 'Queued' | 'Completed' | 'CompletedWithErrors' | 'Failed'
  statusLabel: string
  message: string
  submittedAtUtc: string
  statusUrl: string
}

export type InvoiceDateChangeJobItem = {
  invoiceId: number
  invoiceNo?: string
  status: string
  statusLabel: string
  errorCode?: string
  errorMessage?: string
  attemptCount: number
  updatedAtUtc: string
}

export type InvoiceDateChangeJobStatus = {
  operationId: string
  jobId: string | number
  logId: number
  status: string
  statusLabel: string
  stage: string
  message: string
  totalCount?: number
  pendingCount: number
  awaitingTaxIdCount?: number
  inProgressCount: number
  successCount: number
  failedCount: number
  rejectedCount: number
  dateChangedCount?: number
  taxIdPendingCount?: number
  progressPercent?: number
  submittedAtUtc: string
  selectionCapturedAtUtc?: string
  startedAtUtc?: string
  completedAtUtc?: string
  updatedAtUtc: string
  isTerminal: boolean
  page: number
  pageSize: number
  discoveredCount: number
  items: InvoiceDateChangeJobItem[]
}

const getInvoiceListSummary = (
  baseMode?: number,
  invoiceSeason?: string
): Promise<InvoiceListSummary> => {
  const params = new URLSearchParams()
  if (baseMode) {
    params.set('basemode', baseMode.toString())
  }
  if (invoiceSeason) {
    params.set('invoiceSeason', invoiceSeason)
  }

  return axios
    .get(`${INVOICE_URL}/summary${params.toString() ? `?${params.toString()}` : ''}`)
    .then((d: AxiosResponse<InvoiceListSummary>) => d.data)
}

// The sales list's send-status cards for one season; counted live on the server.
const getInvoiceListSendStatuses = (
  invoiceSeason?: string,
  signal?: AbortSignal
): Promise<InvoiceListSendStatuses> => {
  const params = new URLSearchParams()
  if (invoiceSeason) {
    params.set('invoiceSeason', invoiceSeason)
  }

  return axios
    .get(`${INVOICE_URL}/summary/statuses${params.toString() ? `?${params.toString()}` : ''}`, {
      signal,
    })
    .then((d: AxiosResponse<InvoiceListSendStatuses>) => d.data)
}

const getInvoiceById = (id: ID): Promise<Invoice | undefined> => {
  return axios
    .get(`${INVOICE_URL}/${id}`)
    .then((response: AxiosResponse<Response<Invoice>>) => response.data)
    .then((response: Response<Invoice>) => response.data)
}

const getInvoiceByHashCode = (
  hash: string,
  pdfRenderToken?: string
): Promise<Invoice | undefined> => {
  const url = pdfRenderToken ? `${INVOICE_URL}/pdf-render/${hash}` : `${INVOICE_URL}/${hash}`
  return axios
    .get(url, {
      headers: pdfRenderToken ? {Authorization: `InvoicePdf ${pdfRenderToken}`} : undefined,
    })
    .then((response: AxiosResponse<Response<Invoice>>) => response.data)
    .then((response: Response<Invoice>) => response.data)
}

const createInvoice = (invoice: Invoice): Promise<Invoice | undefined> => {
  return axios
    .post(`${INVOICE_URL}/update/`, invoice)
    .then((response: AxiosResponse<Response<Invoice>>) => response.data)
    .then((response: Response<Invoice>) => response.data)
}

// const updateInvoice = (invoice: Invoice): Promise<Invoice | undefined> => {
//   return axios
//     .post(
//       `${INVOICE_URL}/update/${invoice && invoice.id && invoice.id != null ? invoice.id : ''}`,
//       invoice
//     )
//     .then((response: AxiosResponse<Response<Invoice>>) => response.data)
//     .then((response: Response<Invoice>) => response.data)
// }

const getInvoiceBuyer = (companyId: number): Promise<InvoiceBuyer[] | undefined> => {
  return axios
    .get(`${INVOICE_URL}/buyers/${companyId}`)
    .then((response: AxiosResponse<Response<InvoiceBuyer[]>>) => response.data)
    .then((response: Response<InvoiceBuyer[]>) => response.data)
}

const deleteInvoice = (invoiceId: ID): Promise<void> => {
  return axios.delete(`${INVOICE_URL}/${invoiceId}`).then(() => {})
}

const createInvoiceBulkSendOperationId = (): string => {
  const cryptoApi = window.crypto as Crypto & {randomUUID?: () => string}
  if (typeof cryptoApi?.randomUUID === 'function') {
    return cryptoApi.randomUUID()
  }

  const bytes = new Uint8Array(16)
  if (typeof cryptoApi?.getRandomValues === 'function') {
    cryptoApi.getRandomValues(bytes)
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256)
    }
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(
    16,
    20
  )}-${hex.slice(20)}`
}

const normalizeOperationId = (operationId: string) => operationId.replace(/-/g, '').toLowerCase()

const resolveInvoiceBulkSendStatusUrl = (statusUrl: string): string => {
  const path = `${statusUrl || ''}`.trim()
  if (!path) throw new Error('نشانی پیگیری عملیات ارسال در پاسخ سرور موجود نیست.')
  if (/^https?:\/\//i.test(path)) return path

  const configured = (API_URL || '').replace(/\/$/, '')
  const normalizedPath = path.startsWith('api/') ? `/${path}` : path
  if (normalizedPath.startsWith('/api/')) {
    const apiIndex = configured.toLowerCase().lastIndexOf('/api')
    return `${apiIndex >= 0 ? configured.slice(0, apiIndex) : configured}${normalizedPath}`
  }
  return `${configured}/${normalizedPath.replace(/^\//, '')}`
}

const registerInvoiceBulkSend = (
  request: InvoiceBulkSendRequest
): Promise<InvoiceBulkSendAcceptedResponse> => {
  return axios
    .post<InvoiceBulkSendAcceptedResponse>(`${INVOICE_URL}/sendbulk`, request, {
      timeout: INVOICE_BULK_SEND_REGISTER_TIMEOUT_MS,
    })
    .then((response) => {
      const result = response.data
      if (
        response.status !== 202 ||
        !result?.operationId ||
        normalizeOperationId(result.operationId) !== normalizeOperationId(request.operationId) ||
        !result.statusUrl ||
        // CompletedWithUnresolved: finished, but some sent invoices still wait for an inquiry.
        !['Registered', 'Completed', 'CompletedWithErrors', 'CompletedWithUnresolved'].includes(
          result.status
        ) ||
        !result.statusLabel
      ) {
        throw new Error('پاسخ ثبت درخواست ارسال معتبر نیست. وضعیت عملیات را در لاگ‌ها بررسی کنید.')
      }
      return result
    })
}

const getInvoiceBulkSendJobStatus = (
  statusUrl: string,
  page = 1,
  pageSize = 25,
  signal?: AbortSignal
): Promise<InvoiceBulkSendJobStatus> => {
  return axios
    .get<InvoiceBulkSendJobStatus>(resolveInvoiceBulkSendStatusUrl(statusUrl), {
      params: {page, pageSize},
      signal,
      timeout: INVOICE_BULK_SEND_STATUS_TIMEOUT_MS,
    })
    .then((response) => response.data)
}

const registerInvoiceDateChange = (
  request: InvoiceDateChangeRequest
): Promise<InvoiceDateChangeAcceptedResponse> =>
  axios
    .post<InvoiceDateChangeAcceptedResponse>(`${INVOICE_URL}/changeDatebulk`, request, {
      timeout: INVOICE_DATE_CHANGE_REGISTER_TIMEOUT_MS,
    })
    .then((response) => {
      const result = response.data
      if (
        response.status !== 202 ||
        !result?.operationId ||
        normalizeOperationId(result.operationId) !== normalizeOperationId(request.operationId) ||
        !result.statusUrl ||
        !['Queued', 'Completed', 'CompletedWithErrors', 'Failed'].includes(result.status) ||
        !result.statusLabel
      ) {
        throw new Error(
          'پاسخ ثبت درخواست تغییر تاریخ معتبر نیست. وضعیت عملیات را در لاگ‌ها بررسی کنید.'
        )
      }
      return result
    })

const getInvoiceDateChangeJobStatus = (
  statusUrl: string,
  page = 1,
  pageSize = 25,
  signal?: AbortSignal
): Promise<InvoiceDateChangeJobStatus> =>
  axios
    .get<InvoiceDateChangeJobStatus>(resolveInvoiceBulkSendStatusUrl(statusUrl), {
      params: {page, pageSize},
      signal,
      timeout: INVOICE_DATE_CHANGE_STATUS_TIMEOUT_MS,
    })
    .then((response) => response.data)

const sendSelectedInvoices = (invoiceIds: Array<ID>, token: string): Promise<void> => {
  return axios.post(`${INVOICE_URL}/send/`, {invoices: invoiceIds, token}).then()
}
const sendFilterInvoices = (query: any): Promise<void> => {
  return axios.post(`${INVOICE_URL}/sendFilter/`, query).then()
}
const deleteSelectedInvoices = (invoiceIds: Array<ID>, token: string): Promise<void> => {
  return axios.post(`${INVOICE_URL}/delete/`, {invoices: invoiceIds, token}).then()
}
const importTaxFolderExcel = (file: File): Promise<TaxFolderImportResult> => {
  const formData = new FormData()
  formData.append('file', file)

  return axios
    .post(`${INVOICE_URL}/tax-folder/import`, formData)
    .then((response: AxiosResponse<TaxFolderImportResult>) => response.data)
}
const previewTaxFolderExcel = (file: File): Promise<TaxFolderImportResult> => {
  const formData = new FormData()
  formData.append('file', file)

  return axios
    .post(`${INVOICE_URL}/tax-folder/excel/preview`, formData)
    .then((response: AxiosResponse<TaxFolderImportResult>) => response.data)
}
const confirmTaxFolderExcel = (token: string): Promise<TaxFolderImportResult> => {
  return axios
    .post(`${INVOICE_URL}/tax-folder/excel/confirm`, {token})
    .then((response: AxiosResponse<TaxFolderImportResult>) => response.data)
}
const validateTaxFolderTaxId = (
  taxId: string,
  subject = 1
): Promise<TaxIdOwnershipValidationResult> => {
  return axios
    .get(`${INVOICE_URL}/tax-folder/tax-id/validate`, {params: {taxId, subject}})
    .then((response: AxiosResponse<TaxIdOwnershipValidationResult>) => response.data)
}
const prepareReferenceAction = (
  invoiceId: ID,
  action: 2 | 3 | 4,
  options: {acceptCachedStatus?: boolean; forceRefresh?: boolean} = {}
): Promise<ReferenceActionPreparationResult> => {
  return axios
    .post(`${INVOICE_URL}/reference-actions/prepare`, {invoiceId, action, ...options})
    .then((response: AxiosResponse<ReferenceActionPreparationResult>) => response.data)
}
const inquireInvoiceSendResult = (invoiceId: ID): Promise<InvoiceSendResultInquiryResult> => {
  return axios
    .post(`${INVOICE_URL}/inquery/`, {invoices: [invoiceId]})
    .then((response: AxiosResponse<InvoiceSendResultInquiryResult>) => response.data)
}

const syncSwitchRegistrations = (afterInvoiceId: number): Promise<SwitchRegistrationSyncResult> => {
  return axios
    .post(`${INVOICE_URL}/switch-sync`, {afterInvoiceId})
    .then((response: AxiosResponse<SwitchRegistrationSyncResult>) => response.data)
}
/** One invoice's answer of «استعلام وضعیت کارپوشه» (POST api/invoices/statusInquiry). */
export type InvoiceWorkspaceStatusResult = {
  invoiceId?: number
  taxId?: string
  statusInquiry?: string
  buyerStatusText?: string
  error?: string
  errorCode?: string
}

/**
 * «استعلام وضعیت کارپوشه» for several invoices at once. The server records each answer and settles their chains,
 * so an accepted cancellation puts the invoice it cancels in the cancelled phase.
 */
const inquireInvoiceWorkspaceStatus = (invoiceIds: number[]): Promise<InvoiceWorkspaceStatusResult[]> => {
  return axios
    .post(`${INVOICE_URL}/statusInquiry/`, {invoices: invoiceIds})
    .then((response: AxiosResponse<InvoiceWorkspaceStatusResult[]>) => response.data || [])
}
/** The buyer's reaction a cancelled invoice had before the cancellation (backend 1.0.0.695). */
export type BuyerStatusBeforeCancellation = {
  statusInquiry: string
  statusText?: string
  answeredAt?: string
}

/** Null when the invoice's inquiry history holds no answer from before the cancellation. */
const getBuyerStatusBeforeCancellation = (invoiceId: ID): Promise<BuyerStatusBeforeCancellation | null> => {
  return axios
    .get(`${INVOICE_URL}/${invoiceId}/buyer-status-before-cancellation`)
    .then(
      (response: AxiosResponse<{data: BuyerStatusBeforeCancellation | null}>) => response.data?.data ?? null
    )
}
const getTaxIdConflictRenewal = (invoiceId: ID): Promise<TaxIdConflictRenewalStatus> => {
  return axios
    .get(`${INVOICE_URL}/${invoiceId}/tax-id-conflict-renewal`)
    .then((response: AxiosResponse<{data: TaxIdConflictRenewalStatus}>) => response.data.data)
}
const renewTaxIdAfterConflict = (invoiceId: ID): Promise<TaxIdConflictRenewalResult> => {
  return axios
    .post(`${INVOICE_URL}/${invoiceId}/tax-id-conflict-renewal`)
    .then((response: AxiosResponse<{data: TaxIdConflictRenewalResult}>) => response.data.data)
}
const getExchangeAnnouncementStatus = (
  invoiceId: ID
): Promise<InvoiceExchangeAnnouncementStatusResult> => {
  return axios
    .get(`${INVOICE_URL}/${invoiceId}/exchange-announcement`)
    .then((response: AxiosResponse<InvoiceExchangeAnnouncementStatusResult>) => response.data)
}
const setExchangeAnnouncementStatus = (
  invoiceId: ID,
  {status, note, expectedVersion}: SetExchangeAnnouncementStatusRequest
): Promise<InvoiceExchangeAnnouncementStatusResult> => {
  const trimmedNote = note?.trim()
  return axios
    .post(`${INVOICE_URL}/${invoiceId}/exchange-announcement`, {
      status,
      ...(trimmedNote ? {note: trimmedNote} : {}),
      ...(expectedVersion ? {expectedVersion} : {}),
    })
    .then((response: AxiosResponse<InvoiceExchangeAnnouncementStatusResult>) => response.data)
}
const readTaxFolderReferences = (file: File): Promise<TaxFolderReferenceResult> => {
  const formData = new FormData()
  formData.append('file', file)

  return axios
    .post(`${INVOICE_URL}/tax-folder/references`, formData)
    .then((response: AxiosResponse<TaxFolderReferenceResult>) => response.data)
}
// Registers the header and status of an invoice that is not in the panel from its row of the same
// Excel export, so a correction or cancellation can refer to it.
const registerTaxFolderReference = (
  file: File,
  taxId: string,
  subject: number
): Promise<TaxFolderReferenceRegisterResult> => {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('taxId', taxId)
  formData.append('subject', String(subject))

  return axios
    .post(`${INVOICE_URL}/tax-folder/references/register`, formData)
    .then((response: AxiosResponse<TaxFolderReferenceRegisterResult>) => response.data)
}
const previewTaxFolderPdf = (file: File): Promise<TaxFolderPdfPreviewResult> => {
  const formData = new FormData()
  formData.append('file', file)

  return axios
    .post(`${INVOICE_URL}/tax-folder/pdf/preview`, formData)
    .then((response: AxiosResponse<TaxFolderPdfPreviewResult>) => response.data)
}
const confirmTaxFolderPdf = (token: string): Promise<TaxFolderPdfConfirmResult> => {
  return axios
    .post(`${INVOICE_URL}/tax-folder/pdf/confirm`, {token})
    .then((response: AxiosResponse<TaxFolderPdfConfirmResult>) => response.data)
}
const previewTaxFolderArchive = (file: File): Promise<TaxFolderArchiveImportResult> => {
  const formData = new FormData()
  formData.append('file', file)

  return axios
    .post(`${INVOICE_URL}/tax-folder/archive/preview`, formData)
    .then((response: AxiosResponse<TaxFolderArchiveImportResult>) =>
      normalizeTaxFolderArchiveResult(response.data)
    )
}
const confirmTaxFolderArchive = (
  request: TaxFolderArchiveConfirmRequest
): Promise<TaxFolderArchiveImportResult> => {
  return axios
    .post(`${INVOICE_URL}/tax-folder/archive/confirm`, request)
    .then((response: AxiosResponse<TaxFolderArchiveImportResult>) =>
      normalizeTaxFolderArchiveResult(response.data)
    )
}
// The background import of a confirmed ZIP: its progress, and every row once it has finished.
const getTaxFolderArchiveJob = (jobId: string): Promise<TaxFolderArchiveImportResult> => {
  return axios
    .get(`${INVOICE_URL}/tax-folder/archive/jobs/${encodeURIComponent(jobId)}`)
    .then((response: AxiosResponse<TaxFolderArchiveImportResult>) =>
      normalizeTaxFolderArchiveResult(response.data)
    )
}
// The business's ZIP import still under way (or finished in the last half hour); undefined when there is none.
const getActiveTaxFolderArchiveJob = (): Promise<TaxFolderArchiveImportResult | undefined> => {
  return axios
    .get(`${INVOICE_URL}/tax-folder/archive/active-job`)
    .then((response: AxiosResponse<TaxFolderArchiveImportResult | null>) =>
      response.data && response.data.jobId
        ? normalizeTaxFolderArchiveResult(response.data)
        : undefined
    )
}
const exportExcelInvoices = (query: any): Promise<void> => {
  return axios.post(`${INVOICE_URL}/Export/Excel/`, query).then(() => undefined)
}
// Each invoice's PDF is printed with its business's print page; the new page's layout version names
// its cached PDFs, so a changed layout is rendered afresh.
const exportPdfInvoices = (query: any): Promise<InvoicesQueryResponse> => {
  return axios
    .post(`${INVOICE_URL}/Export/Pdf/`, {...query, printLayout: PRINT_LAYOUT_VERSION})
    .then((d: AxiosResponse<InvoicesQueryResponse>) => d.data)
}

const getDownloadFileName = (contentDisposition?: string, fallbackFileName?: string): string => {
  const utf8FileName = contentDisposition?.match(/filename\*=UTF-8''([^;]+)/i)?.[1]
  const asciiFileName = contentDisposition?.match(/filename="?([^";]+)"?/i)?.[1]
  const fileName = utf8FileName || asciiFileName
  if (fileName) {
    try {
      return decodeURIComponent(fileName)
    } catch {
      return fileName
    }
  }

  return fallbackFileName || 'invoice.pdf'
}

const downloadBlobResponse = (response: AxiosResponse<Blob>, fallbackFileName: string) => {
  const href = URL.createObjectURL(response.data)
  const link = document.createElement('a')
  link.href = href
  link.setAttribute(
    'download',
    getDownloadFileName(response.headers['content-disposition'], fallbackFileName)
  )
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(href)
}

/**
 * The invoice's PDF. Without a page it is printed with its business's print page; a print page
 * asks for its own (the earlier page passes 'classic'), so the PDF is the page the user sees.
 */
const downloadInvoicePdf = (
  hash: string,
  fallbackFileName = 'invoice.pdf',
  options: {page?: InvoicePrintPage} = {}
): Promise<void> => {
  return axios
    .get(`${INVOICE_URL}/pdf/${hash}`, {
      responseType: 'blob',
      params: {layout: PRINT_LAYOUT_VERSION, page: options.page},
    })
    .then((response: AxiosResponse<Blob>) => {
      downloadBlobResponse(response, fallbackFileName)
    })
}

export {
  getInvoices,
  getSubInvoices,
  deleteInvoice,
  deleteSelectedInvoices,
  getInvoiceById,
  getInvoiceByHashCode,
  createInvoice,
  //updateInvoice,
  createInvoiceBulkSendOperationId,
  registerInvoiceBulkSend,
  getInvoiceBulkSendJobStatus,
  registerInvoiceDateChange,
  getInvoiceDateChangeJobStatus,
  sendSelectedInvoices,
  sendFilterInvoices,
  getInvoiceBuyer,
  exportExcelInvoices,
  exportPdfInvoices,
  downloadInvoicePdf,
  importTaxFolderExcel,
  previewTaxFolderExcel,
  confirmTaxFolderExcel,
  validateTaxFolderTaxId,
  prepareReferenceAction,
  inquireInvoiceSendResult,
  syncSwitchRegistrations,
  inquireInvoiceWorkspaceStatus,
  getBuyerStatusBeforeCancellation,
  getTaxIdConflictRenewal,
  renewTaxIdAfterConflict,
  getExchangeAnnouncementStatus,
  setExchangeAnnouncementStatus,
  readTaxFolderReferences,
  registerTaxFolderReference,
  previewTaxFolderPdf,
  confirmTaxFolderPdf,
  previewTaxFolderArchive,
  confirmTaxFolderArchive,
  getTaxFolderArchiveJob,
  getActiveTaxFolderArchiveJob,
  getInvoiceListSummary,
  getInvoiceListSummaryQueryKey,
  getInvoiceListSendStatuses,
  getInvoiceListSendStatusesQueryKey,
  getInvoiceFiscalMemories,
  getInvoiceFiscalMemoriesQueryKey,
  shouldRetryInvoiceListSummary,
  INVOICE_LIST_SUMMARY_STALE_TIME_MS,
  INVOICE_LIST_SUMMARY_CACHE_TIME_MS,
}
